import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, navigate]);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-24 mb-8" />
            <div className="h-[400px] bg-gray-200 rounded-2xl mb-8" />
            <div className="space-y-4">
              <div className="h-10 bg-gray-200 rounded w-3/4" />
              <div className="h-6 bg-gray-200 rounded w-1/2" />
              <div className="h-64 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!post) return null;

  const title = language === 'it' ? post.title_it : post.title_en;
  const content = language === 'it' ? post.content_it : post.content_en;

  const categoryLabels = {
    tips: language === 'it' ? 'Consigli' : 'Tips',
    destinations: language === 'it' ? 'Destinazioni' : 'Destinations',
    food: language === 'it' ? 'Cibo' : 'Food'
  };

  return (
    <main className="pt-20 min-h-screen bg-white" data-testid="blog-post-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={post.image_url}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      <article className="max-w-4xl mx-auto px-6 md:px-12 -mt-32 relative z-10">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-8"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-5 h-5" />
          {t.nav.blog}
        </motion.button>

        {/* Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
        >
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-[#86868B]">
            <span className="flex items-center gap-1.5">
              <Tag className="w-4 h-4" />
              <Link 
                to={`/blog?category=${post.category}`}
                className="text-[#C9B896] hover:underline"
              >
                {categoryLabels[post.category] || post.category}
              </Link>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {post.author}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-['Manrope'] font-bold tracking-tight text-[#1D1D1F] text-3xl md:text-5xl mb-8">
            {title}
          </h1>

          {/* Content */}
          <div className="prose prose-lg max-w-none text-[#1D1D1F]">
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-2xl font-bold mt-8 mb-4 text-[#1D1D1F]">{children}</h2>,
                h3: ({children}) => <h3 className="text-xl font-semibold mt-6 mb-3 text-[#1D1D1F]">{children}</h3>,
                p: ({children}) => <p className="mb-4 leading-relaxed text-[#333]">{children}</p>,
                strong: ({children}) => <strong className="font-semibold text-[#1D1D1F]">{children}</strong>,
                em: ({children}) => <em className="italic text-[#555]">{children}</em>,
                ul: ({children}) => <ul className="list-disc list-inside mb-4 space-y-2">{children}</ul>,
                li: ({children}) => <li className="text-[#333]">{children}</li>,
                a: ({href, children}) => <a href={href} className="text-[#C9B896] hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center py-16"
        >
          <p className="text-[#86868B] mb-4">
            {language === 'it' 
              ? 'Vuoi esplorare di più?' 
              : 'Want to explore more?'}
          </p>
          <Link
            to="/guides"
            className="inline-flex items-center gap-2 bg-[#C9B896] text-white px-8 py-4 rounded-full font-medium hover:bg-[#A89770] transition-colors"
            data-testid="cta-explore-guides"
          >
            {t.hero.cta}
          </Link>
        </motion.div>
      </article>
    </main>
  );
};

export default BlogPost;
